import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSchool } from "@/context/SchoolContext";
import { hasPerm, initials } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Info, Pencil, Save, X, Plus, Trash2, Upload, Loader2, GraduationCap, Rows3, Columns3 } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_ABOUT = {
  app_info: "SSSPS is a modern, secure and easy-to-use School Management & Fee Management System. It brings student and staff management, advanced fee collection, payroll, transport, attendance, examinations and detailed reporting together in one clean, role-based platform built for schools.",
  developers: [
    { name: "Dr Ranjeet Singh", qualification: "MCA", role: "", photo: "" },
    { name: "Ravijeet Singh", qualification: "MCA", role: "", photo: "" },
    { name: "Ajeet Kumar", qualification: "B. Tech IITM", role: "Working professional at Google", photo: "" },
  ],
};

export default function AboutUs() {
  const { user } = useAuth();
  const { school, refreshSchool } = useSchool();
  const canEdit = hasPerm(user, "manage_settings");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [devLayout, setDevLayout] = useState(() => localStorage.getItem("about_dev_layout") || "horizontal");
  const [form, setForm] = useState(DEFAULT_ABOUT);

  const toggleLayout = () => setDevLayout((l) => { const n = l === "horizontal" ? "vertical" : "horizontal"; localStorage.setItem("about_dev_layout", n); return n; });

  useEffect(() => {
    const a = school?.about_us;
    if (a && (a.app_info || (a.developers && a.developers.length))) setForm({ app_info: a.app_info || "", developers: a.developers || [] });
    else setForm(DEFAULT_ABOUT);
  }, [school]);

  const setDev = (i, key, val) => setForm((f) => ({ ...f, developers: f.developers.map((d, idx) => idx === i ? { ...d, [key]: val } : d) }));
  const addDev = () => setForm((f) => ({ ...f, developers: [...f.developers, { name: "", qualification: "", role: "", photo: "" }] }));
  const removeDev = (i) => setForm((f) => ({ ...f, developers: f.developers.filter((_, idx) => idx !== i) }));

  const uploadPhoto = (i, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Photo must be under 2MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setDev(i, "photo", reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { ...school, about_us: form });
      await refreshSchool();
      toast.success("About Us updated");
      setEditing(false);
    } catch (e) { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const cancel = () => {
    const a = school?.about_us;
    setForm(a && (a.app_info || (a.developers && a.developers.length)) ? { app_info: a.app_info || "", developers: a.developers || [] } : DEFAULT_ABOUT);
    setEditing(false);
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 flex items-center gap-2"><Info className="text-indigo-500" size={26} />About Us</h1>
          <p className="text-slate-500">About this application and the team behind it.</p>
        </div>
        {canEdit && !editing && (
          <Button variant="outline" className="gap-1.5" onClick={() => setEditing(true)} data-testid="about-edit-btn"><Pencil size={16} />Edit</Button>
        )}
        {canEdit && editing && (
          <div className="flex items-center gap-2">
            <Button variant="ghost" className="gap-1.5" onClick={cancel} data-testid="about-cancel-btn"><X size={16} />Cancel</Button>
            <Button className="gap-1.5" onClick={save} disabled={saving} data-testid="about-save-btn">{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Save</Button>
          </div>
        )}
      </div>

      <section className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="font-heading font-bold text-lg text-slate-900 mb-3">The Application</h2>
        {editing ? (
          <Textarea rows={6} value={form.app_info} onChange={(e) => setForm((f) => ({ ...f, app_info: e.target.value }))} placeholder="Write about the application…" data-testid="about-appinfo-input" />
        ) : (
          <p className="text-slate-600 leading-relaxed whitespace-pre-wrap" data-testid="about-appinfo-text">{form.app_info}</p>
        )}
      </section>

      <section className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-lg text-slate-900">Developers</h2>
          <div className="flex items-center gap-2">
            {!editing && (
              <Button size="sm" variant="outline" className="gap-1.5" onClick={toggleLayout} data-testid="about-dev-layout-btn" title={devLayout === "horizontal" ? "Switch to vertical layout" : "Switch to horizontal layout"}>
                {devLayout === "horizontal" ? <Rows3 size={15} /> : <Columns3 size={15} />}{devLayout === "horizontal" ? "Vertical" : "Horizontal"}
              </Button>
            )}
            {editing && <Button size="sm" variant="outline" className="gap-1.5" onClick={addDev} data-testid="about-add-dev-btn"><Plus size={15} />Add Developer</Button>}
          </div>
        </div>

        {!editing ? (
          <div className={devLayout === "horizontal" ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" : "flex flex-col gap-4"}>
            {form.developers.map((d, i) => (
              devLayout === "horizontal" ? (
                <div key={i} className="rounded-xl border border-slate-200 p-5 flex flex-col items-center text-center hover:shadow-md transition-shadow" data-testid={`about-dev-${i}`}>
                  {d.photo ? <img src={d.photo} alt={d.name} className="w-24 h-24 rounded-full object-cover ring-4 ring-indigo-50" /> : <div className="w-24 h-24 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-2xl font-bold">{initials(d.name) || <GraduationCap size={28} />}</div>}
                  <h3 className="mt-3 font-semibold text-slate-900">{d.name}</h3>
                  {d.qualification && <p className="text-sm text-indigo-600 font-medium">{d.qualification}</p>}
                  {d.role && <p className="text-sm text-slate-500 mt-1">{d.role}</p>}
                </div>
              ) : (
                <div key={i} className="rounded-xl border border-slate-200 p-4 flex items-center gap-4 hover:shadow-md transition-shadow" data-testid={`about-dev-${i}`}>
                  {d.photo ? <img src={d.photo} alt={d.name} className="w-16 h-16 rounded-full object-cover ring-4 ring-indigo-50 shrink-0" /> : <div className="w-16 h-16 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xl font-bold shrink-0">{initials(d.name) || <GraduationCap size={24} />}</div>}
                  <div>
                    <h3 className="font-semibold text-slate-900">{d.name}</h3>
                    {d.qualification && <p className="text-sm text-indigo-600 font-medium">{d.qualification}</p>}
                    {d.role && <p className="text-sm text-slate-500">{d.role}</p>}
                  </div>
                </div>
              )
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {form.developers.map((d, i) => (
              <div key={i} className="rounded-xl border border-slate-200 p-4 flex flex-col sm:flex-row gap-4" data-testid={`about-dev-edit-${i}`}>
                <div className="flex flex-col items-center gap-2 shrink-0">
                  {d.photo ? <img src={d.photo} alt={d.name} className="w-20 h-20 rounded-full object-cover" /> : <div className="w-20 h-20 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xl font-bold">{initials(d.name) || "?"}</div>}
                  <label className="text-xs text-indigo-600 font-medium cursor-pointer inline-flex items-center gap-1">
                    <Upload size={13} />Photo
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(i, e.target.files?.[0])} data-testid={`about-dev-photo-${i}`} />
                  </label>
                </div>
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div><Label>Name</Label><Input value={d.name} onChange={(e) => setDev(i, "name", e.target.value)} data-testid={`about-dev-name-${i}`} /></div>
                  <div><Label>Qualification</Label><Input value={d.qualification} onChange={(e) => setDev(i, "qualification", e.target.value)} data-testid={`about-dev-qual-${i}`} /></div>
                  <div><Label>Role / Note</Label><Input value={d.role} onChange={(e) => setDev(i, "role", e.target.value)} data-testid={`about-dev-role-${i}`} /></div>
                </div>
                <Button size="icon" variant="ghost" className="text-rose-500 self-start" onClick={() => removeDev(i)} data-testid={`about-dev-remove-${i}`}><Trash2 size={16} /></Button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
